import { SearchIcon } from "@chakra-ui/icons";
import {
  Box,
  Divider,
  Flex,
  Heading,
  Input,
  InputGroup,
  InputLeftElement,
  Text,
} from "@chakra-ui/react";
import { Facility } from "@metriport/api-sdk/medical/models/facility";
import { useQuery } from "@tanstack/react-query";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { getCohorts, Cohort } from "../../../api/cohort";
import { TcmEncounter, TcmEncounterResponse } from "../../../api/tcm-encounter";
import { handleTimeoutOrNetworkErrorWithTryAgainToast } from "../../error/utils";
import { csvColumn, useCSVExport } from "../../../hooks/use-csv-export";
import { useAnalyticsContext, Actions, Features } from "../shared-logic/analytics-context";
import { SidePanel } from "../shared-components/side-panel/side-panel";
import { useMetriportApi } from "../shared-logic/useMetriportApi";
import useMetriportToast from "../shared-logic/useMetriportToast";
import { Button } from "../shared-components/button";
import { METRIPORT_PRIMARY } from "../shared-logic/style";
import { GET_TCM_ENCOUNTER_DEFAULT_COUNT } from "./api";
import FilterBar, {
  classDefaultOption,
  ClassOption,
  codingDefaultOption,
  CodingOption,
  cohortDefaultOption,
  CohortOption,
  DaysOption,
  FacilityOption,
  filterDefaultOption,
  GetTcmEncounterQueryParams,
  statusDefaultOption,
  StatusOption,
  typeDefaultOption,
  TypeOption,
} from "./filters";
import TransitionsOfCareGrid, { TransitionsOfCareGridRef } from "./grid";
import { createUpdatedEncounterData, OutreachAction } from "./outreach-actions";
import { useTcmMutations } from "./tcm-mutations";
import { useTcmApi } from "./useTcmApi";
import { EncounterDetails } from "../encounter/encounter-details";

const enterKeys = ["Enter", "NumpadEnter"];

export type TransitionsOfCareContentProps = {
  onNavigateToPatient?: (patientId: string) => void;
  isImpersonating?: boolean;
  capture?: {
    error: (msg: string, options?: { extra?: Record<string, unknown> }) => void;
  };
};

export function TransitionsOfCareContent({
  onNavigateToPatient,
  isImpersonating = false,
  capture,
}: TransitionsOfCareContentProps) {
  const tcmApi = useTcmApi();
  const { onSummaryFilePathChange, updateNoteMutation, updateOutreachLogMutation } =
    useTcmMutations(tcmApi);
  const toast = useMetriportToast();

  const [isLoading, setIsLoading] = useState(false);
  const [searchText, setSearchText] = useState<string>("");
  const [totalCountOfTcmEncounters, setTotalCountOfTcmEncounters] = useState<number | undefined>(
    undefined
  );
  const [tableFromTop, setTableFromTopHeight] = useState(0);
  const [showSidePanel, setShowSidePanel] = useState(false);

  const [selectedFacility, setSelectedFacility] = useState<FacilityOption | undefined>();
  const [selectedCohort, setSelectedCohort] = useState<CohortOption>(cohortDefaultOption);
  const [selectedType, setSelectedType] = useState<TypeOption>(typeDefaultOption);
  const [selectedDays, setSelectedDays] = useState<DaysOption>(filterDefaultOption);
  const [selectedCoding, setSelectedCoding] = useState<CodingOption>(codingDefaultOption);
  const [selectedStatus, setSelectedStatus] = useState<StatusOption>(statusDefaultOption);
  const [selectedClass, setSelectedClass] = useState<ClassOption>(classDefaultOption);

  // Enhanced filter handlers that trigger grid reset
  const handleFacilitySelect = useCallback(
    (facility: FacilityOption | undefined) => {
      setSelectedFacility(facility);
      const newFilters = {
        facility,
        cohortId: selectedCohort,
        type: selectedType,
        days: selectedDays,
        coding: selectedCoding,
        status: selectedStatus,
        class: selectedClass,
      };
      gridRef?.current?.resetSearch(newFilters);
    },
    [selectedCohort, selectedType, selectedDays, selectedCoding, selectedStatus, selectedClass]
  );

  const handleCohortSelect = useCallback(
    (cohort: CohortOption) => {
      setSelectedCohort(cohort);
      const newFilters = {
        facility: selectedFacility,
        cohortId: cohort,
        type: selectedType,
        days: selectedDays,
        coding: selectedCoding,
        status: selectedStatus,
        class: selectedClass,
      };
      gridRef?.current?.resetSearch(newFilters);
    },
    [selectedFacility, selectedType, selectedDays, selectedCoding, selectedStatus, selectedClass]
  );

  const handleTypeSelect = useCallback(
    (type: TypeOption) => {
      setSelectedType(type);
      const newFilters = {
        facility: selectedFacility,
        cohortId: selectedCohort,
        type,
        days: selectedDays,
        coding: selectedCoding,
        status: selectedStatus,
        class: selectedClass,
      };
      gridRef?.current?.resetSearch(newFilters);
    },
    [selectedFacility, selectedCohort, selectedDays, selectedCoding, selectedStatus, selectedClass]
  );

  const handleDaySelect = useCallback(
    (days: DaysOption) => {
      setSelectedDays(days);
      const newFilters = {
        facility: selectedFacility,
        cohortId: selectedCohort,
        type: selectedType,
        days,
        coding: selectedCoding,
        status: selectedStatus,
        class: selectedClass,
      };
      gridRef?.current?.resetSearch(newFilters);
    },
    [selectedFacility, selectedCohort, selectedType, selectedCoding, selectedStatus, selectedClass]
  );

  const handleCodingSelect = useCallback(
    (coding: CodingOption) => {
      setSelectedCoding(coding);
      const newFilters = {
        facility: selectedFacility,
        cohortId: selectedCohort,
        type: selectedType,
        days: selectedDays,
        coding,
        status: selectedStatus,
        class: selectedClass,
      };
      gridRef?.current?.resetSearch(newFilters);
    },
    [selectedFacility, selectedCohort, selectedType, selectedDays, selectedStatus, selectedClass]
  );

  const handleStatusSelect = useCallback(
    (status: StatusOption) => {
      setSelectedStatus(status);
      const newFilters = {
        facility: selectedFacility,
        cohortId: selectedCohort,
        type: selectedType,
        days: selectedDays,
        coding: selectedCoding,
        status,
        class: selectedClass,
      };
      gridRef?.current?.resetSearch(newFilters);
    },
    [selectedFacility, selectedCohort, selectedType, selectedDays, selectedCoding, selectedClass]
  );

  const handleClassSelect = useCallback(
    (classData: ClassOption) => {
      setSelectedClass(classData);
      const newFilters = {
        facility: selectedFacility,
        cohortId: selectedCohort,
        type: selectedType,
        days: selectedDays,
        coding: selectedCoding,
        status: selectedStatus,
        class: classData,
      };
      gridRef?.current?.resetSearch(newFilters);
    },
    [selectedFacility, selectedCohort, selectedType, selectedDays, selectedCoding, selectedStatus]
  );

  const [selectedRow, setSelectedRow] = useState<TcmEncounter | null>(null);

  const Analytics = useAnalyticsContext();
  const metriportApi = useMetriportApi();
  const gridRef = useRef<TransitionsOfCareGridRef | null>(null);

  // Debounce ref for note updates
  const debounceRef = useRef<NodeJS.Timeout>();

  const facilitiesQuery = useQuery<Facility[], Error>({
    queryKey: ["facilities"],
    queryFn: () => metriportApi.listFacilities(),
  });

  const cohortsQuery = useQuery<Cohort[], Error>({
    queryKey: ["cohorts"],
    queryFn: () => getCohorts(),
  });

  const tcmFilters: GetTcmEncounterQueryParams = {
    facility: selectedFacility,
    cohortId: selectedCohort,
    type: selectedType,
    days: selectedDays,
    coding: selectedCoding,
    status: selectedStatus,
    class: selectedClass,
  };

  const getTcmEncountersWithQueryParams = useCallback(
    async (
      pageUrl?: string | undefined,
      localQueryParams?: GetTcmEncounterQueryParams | undefined
    ): Promise<TcmEncounterResponse | undefined> => {
      try {
        setIsLoading(true);
        const response = await tcmApi.getTcmEncounters(pageUrl, localQueryParams);
        if (response.meta?.itemsInTotal != undefined)
          setTotalCountOfTcmEncounters(response.meta.itemsInTotal);
        return response;
      } catch (error) {
        console.error("Error retrieving TCM encounters:", error);
        const { handled } = handleTimeoutOrNetworkErrorWithTryAgainToast(error, toast);
        if (!handled) {
          const msg = "Error retrieving TCM encounters";
          capture?.error(msg, {
            extra: { context: `tcm-encounters.get`, error },
          });
          toast.error({ title: msg });
        }
        return undefined;
      } finally {
        setIsLoading(false);
      }
    },
    [tcmApi, toast, capture]
  );

  const handleNoteUpdate = useCallback(
    (encounterId: string, note: string, isUpdateGridNote: boolean) => {
      // Sync grid note with panel
      const rowNode = gridRef.current?.api?.getRowNode(encounterId);
      if (rowNode && isUpdateGridNote) {
        rowNode.setDataValue("freetextNote", note);
      }

      // Clear existing timeout
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }

      debounceRef.current = setTimeout(() => {
        updateNoteMutation.mutate({ encounterId, note });
      }, 1000);
    },
    [updateNoteMutation]
  );

  const handleOutreachLogUpdate = useCallback(
    (status: "Attempted" | "Completed", action: OutreachAction) => {
      if (!selectedRow || !selectedRow.id) return;

      const rowNode = gridRef.current?.api?.getRowNode(selectedRow.id);
      if (!rowNode) return;

      const updatedData = createUpdatedEncounterData(selectedRow, status, action);
      rowNode.setData(updatedData);
      setSelectedRow(updatedData);

      updateOutreachLogMutation.mutate({
        encounterId: selectedRow.id,
        encounter: updatedData,
      });
    },
    [selectedRow, updateOutreachLogMutation]
  );

  const { exportToCSV: exportCSV } = useCSVExport<TcmEncounter>({
    baseFilename: "tcm-encounters",
  });

  const csvColumns = useMemo(
    () => [
      csvColumn<TcmEncounter>("Patient Name", e => e.patientName),
      csvColumn<TcmEncounter>("Patient DOB", e => e.patientDateOfBirth),
      csvColumn<TcmEncounter>("Facility", e => e.facilityName),
      csvColumn<TcmEncounter>("Latest Event", e => e.latestEvent),
      csvColumn<TcmEncounter>("Class", e => e.class),
      csvColumn<TcmEncounter>("Admit Time", e =>
        e.admitTime ? new Date(e.admitTime).toISOString() : ""
      ),
      csvColumn<TcmEncounter>("Discharge Time", e =>
        e.dischargeTime ? new Date(e.dischargeTime).toISOString() : ""
      ),
      csvColumn<TcmEncounter>("Outreach Status", e => e.outreachStatus),
      csvColumn<TcmEncounter>("Last Outreach Date", e =>
        e.lastOutreachDate ? new Date(e.lastOutreachDate).toISOString() : ""
      ),
      csvColumn<TcmEncounter>("Note", e => e.freetextNote ?? ""),
      csvColumn<TcmEncounter>("Phone Numbers", e => e.patientPhoneNumbers.join("; ")),
    ],
    []
  );

  const handleExportToCSV = useCallback(async () => {
    try {
      setIsLoading(true);

      // Fetch all encounters with current filters (no pagination limit)
      const allEncounters: TcmEncounter[] = [];
      let nextPageUrl: string | undefined = undefined;
      let hasMore = true;

      while (hasMore) {
        const response: TcmEncounterResponse | undefined = await getTcmEncountersWithQueryParams(
          nextPageUrl,
          tcmFilters
        );
        if (response?.items) {
          allEncounters.push(...response.items);
        }
        nextPageUrl = response?.meta?.nextPage;
        hasMore = !!nextPageUrl;
      }

      if (allEncounters.length === 0) {
        toast.warning({ title: "No TCM encounters to export" });
        return;
      }

      const count = exportCSV(allEncounters, csvColumns);
      toast.success({ title: `Exported ${count} encounters` });
    } catch (error) {
      capture?.error("Error exporting TCM encounters to CSV", {
        extra: { context: "tcm-encounters.export", error },
      });
      toast.error({ title: "Error exporting TCM encounters to CSV" });
    } finally {
      setIsLoading(false);
    }
  }, [tcmFilters, getTcmEncountersWithQueryParams, toast, exportCSV, csvColumns, capture]);

  useEffect(() => {
    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, []);

  useEffect(() => {
    const element = document.getElementById("table-container");
    if (element) {
      setTableFromTopHeight(element.getBoundingClientRect().top);
    }
  }, []);

  function search() {
    gridRef?.current?.resetSearch({ ...tcmFilters, search: searchText });
  }

  function clearFilters() {
    setSelectedFacility(undefined);
    setSelectedCohort(cohortDefaultOption);
    setSelectedType(typeDefaultOption);
    setSelectedDays(filterDefaultOption);
    setSelectedCoding(codingDefaultOption);
    setSelectedStatus(statusDefaultOption);
    setSelectedClass(classDefaultOption);
    setSearchText("");
    gridRef?.current?.resetSearch({});
  }

  const onRowClick = useCallback((row: TcmEncounter) => {
    setSelectedRow(row);
    setShowSidePanel(true);
  }, []);

  return (
    <Flex p={10}>
      <Box px={5} flexGrow={1}>
        <Flex alignItems={"center"} justifyContent={"space-between"}>
          <Heading size="xl">Patient Transitions of Care</Heading>
        </Flex>
        <Divider my={6} />
        <Flex
          mb={5}
          align={{ base: "stretch", md: "center" }}
          justify="space-between"
          flexWrap="nowrap"
          gap={4}
        >
          <InputGroup>
            <InputLeftElement pointerEvents="none" color="gray.300" fontSize="1.2em">
              <SearchIcon />
            </InputLeftElement>
            <Input
              minW={"300px"}
              w={"35%"}
              value={searchText}
              placeholder="Search encounters"
              onClick={() => Analytics.emit(Actions.search, Features.facility)}
              onChange={e => setSearchText(e.target.value)}
              onKeyDown={key => {
                if (enterKeys.includes(key.code)) search();
              }}
              _hover={{ borderColor: METRIPORT_PRIMARY }}
              _active={{ borderColor: METRIPORT_PRIMARY }}
              _focus={{ borderColor: METRIPORT_PRIMARY }}
            />
            <Button onClick={search} ml={2}>
              Search
            </Button>
            <Button onClick={clearFilters} ml={2}>
              Clear
            </Button>
          </InputGroup>
          <Flex gap={4}>
            <FilterBar
              facilitiesQuery={facilitiesQuery}
              cohortsQuery={cohortsQuery}
              selectedFacility={selectedFacility}
              selectedCohort={selectedCohort}
              selectedType={selectedType}
              selectedDays={selectedDays}
              selectedCoding={selectedCoding}
              selectedStatus={selectedStatus}
              selectedClass={selectedClass}
              handleFacilitySelect={handleFacilitySelect}
              handleCohortSelect={handleCohortSelect}
              handleTypeSelect={handleTypeSelect}
              handleDaySelect={handleDaySelect}
              handleCodingSelect={handleCodingSelect}
              handleStatusSelect={handleStatusSelect}
              handleClassSelect={handleClassSelect}
            />
          </Flex>
          <Button onClick={handleExportToCSV} isLoading={isLoading} size="sm" flexShrink={0}>
            Export CSV
          </Button>
        </Flex>
        <Box
          id="table-container"
          style={{ width: "100%", height: `calc(95vh - ${tableFromTop}px)` }}
        >
          <TransitionsOfCareGrid
            ref={gridRef}
            getTcmEncounters={getTcmEncountersWithQueryParams}
            isLoading={isLoading}
            numItemsPerPage={GET_TCM_ENCOUNTER_DEFAULT_COUNT}
            onRowClick={onRowClick}
            setEventTypeFilter={setSelectedType}
            onNoteUpdate={(encounterId, note) => handleNoteUpdate(encounterId, note, false)}
          />
        </Box>
        <Text align={"right"} pt={3}>
          Total amount of TCM encounters: {totalCountOfTcmEncounters}
        </Text>
      </Box>
      <SidePanel showPanel={showSidePanel} setSidePanelContent={() => setShowSidePanel(false)}>
        {selectedRow && (
          <EncounterDetails
            encounter={selectedRow}
            onNoteChange={note => selectedRow.id && handleNoteUpdate(selectedRow.id, note, true)}
            onSummaryFilePathChange={summaryFilePath =>
              selectedRow.id && onSummaryFilePathChange(selectedRow.id, summaryFilePath)
            }
            onOutreachLogUpdate={handleOutreachLogUpdate}
            onNavigateToPatient={onNavigateToPatient}
            isImpersonating={isImpersonating}
          />
        )}
      </SidePanel>
    </Flex>
  );
}

export default TransitionsOfCareContent;

// Re-export types and utilities
export type { TcmEncounter, TcmEncounterResponse } from "../../../api/tcm-encounter";
export type { GetTcmEncounterQueryParams } from "./filters";
export { createTcmApi, GET_TCM_ENCOUNTER_DEFAULT_COUNT } from "./api";
export type { OutreachAction } from "./outreach-actions";
export { EncounterDetails } from "../encounter/encounter-details";
export type { EncounterDetailsProps } from "../encounter/encounter-details";
